import {getDirForMediaSizeType, MediaItemSizeType, repoURL} from '@Libraries/s3-library';
import {getCompatibleImageFileExtension} from '@Utils/image.util';
import type {PMWStockImageSourceType, PMWStockMediaSourceType} from '@Libraries/pmw-stock-media-library.types';
import {PMW_STOCK_IMAGE_THUMB_EXTENSION} from '@Libraries/pmw-stock-media-library.types';
import {PMW_STOCK_IMAGE_EXTENSION, PMWStockImageSource, PMWStockMediaSource} from '@Libraries/pmw-stock-media-library.types';
import type {
  PMWStockBackendAiIcon,
  PMWStockBackendAiSticker,
  PMWStockBackendAnimatedSticker,
  PMWStockBackendEmoji,
  PMWStockBackendExtractedGettySticker,
  PMWStockBackendIcon,
  PMWStockBackendShape,
  PMWStockBackendStillSticker,
} from '@Panels/manage-pmw-stock-panels/manage-pmw-stock-panels-types';
import type {StockButtonFilters, StockResponseCarousel, CarouselTabTiers} from '@Panels/stock-panel/stock-panel.types';
import {AUDIO_CATEGORIES_REQUEST_LOCAL_STORAGE_CACHE_KEY} from '@Panels/stock-panel/stock-panel.types';
import type {GridItemStorage} from '@Components/base-grid/components/grid-item';
import {isProResSupported, isWebmSupported} from '@Utils/browser.util';
import {getReadBucket, getWriteBucket} from '@Utils/s3.util';
import {GA4EventName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {SOURCE} from '@Libraries/stock-graphic-library';
import type {AiImageModelOption} from '@Panels/ai-image-panel/ai-image-panel.types';
import {fetchStockForCarousel, getStatusForStockCarouselGetRequest} from '@Panels/manage-pmw-stock-panels/pmw-stock-panels-helper';
import {setAiCredits} from '@Components/poster-editor/poster-editor-reducer';

const BASE_DIR = 'pmwstockmedia';
const STICKER_IMAGE_THUMB_EXTENSION = 'webp';
const SHAPE_IMAGE_THUMB_EXTENSION = 'webp';
const SHAPE_IMAGE_THUMB_FILENAME_SUFFIX = '_thumb';
const DEFAULT_STICKER_EXTENSION = 'webm';
const TRANSPARENT_VIDEO_FALLBACK_EXTENSION = 'mov';
const NON_TRANSPARENT_VIDEO_FALLBACK_EXTENSION = 'mp4';
const STICKER_THUMB_EXTENSION = 'gif';
const STICKERS_UPDATED_ON = 1615206102;
const PMW_STOCK_UPDATED_ON = 1764930611;

export const GENERATE_IMAGE_INSUFFICIENT_CREDITS_RESPONSE_CODE = 'insufficient_credits';
export const GENERATE_IMAGE_FLAGGED_PROMPT_RESPONSE_CODE = 'flagged_prompt';
export const GENERATE_IMAGE_VAGUE_PROMPT_RESPONSE_CODE = 'vague_prompt';
export const GENERATE_IMAGE_UNSUPPORTED_LANGUAGE_RESPONSE_CODE = 'stability_ai_unsupported_language';

export enum StockSources {
  PMW_ICON = 'icon',
  PMW_STILL_STICKER = 'still_sticker',
  PMW_EXTRACTED_GETTY_STICKER = 'extracted_getty_sticker',
  PMW_AI_STICKER = 'ai_sticker',
  PMW_AI_ICON = 'ai_icon',
  PMW_EMOJI = 'emoji',
  PMW_STOCK_IMAGE = 'image',
  PMW_ANIMATED_STICKER = 'animated_sticker',
  PMW_AI_ANIMATED_STICKER = 'ai_animated_sticker',
  PMW_SHAPE = 'shape',
  GETTY_IMAGE = 'GETTY_IMAGE',
  GETTY_VIDEO = 'GETTY_VIDEO',
  GETTY_ILLUSTRATION = 'GETTY_ILLUSTRATION',
  STORYBLOCK_IMAGE = 'STORYBLOCK_IMAGE',
  STORYBLOCK_VIDEO = 'STORYBLOCK_VIDEO',
  STORYBLOCK_ILLUSTRATION = 'STORYBLOCK_ILLUSTRATION',
  STORYBLOCK_AUDIO = 'STORYBLOCK_AUDIO',
  PIXABY_IMAGE = 'PIXABY_IMAGE',
  PIXABY_VIDEO = 'PIXABY_VIDEO',
  PIXABY_ILLUSTRATION = 'PIXABY_ILLUSTRATION',
}

export type StockOffsets = Record<StockSources, number>;

export type LazyLoadingOffsets = Record<any, number>;

export interface SubCategoryBackendData {
  displayName: string;
  id: number;
  idCategory: string;
}

export interface AudioCategoryBackendData {
  contentType: string;
  displayName: string;
  id: string;
  subcategories: SubCategoryBackendData[];
}

export interface StockGetterOptions {
  audioFilters?: Record<StockButtonFilters, string[]>;
  only4KVideos?: boolean;
  iconCategories?: string[];
  shapeCategories?: string[];
  seasonalSearch?: string;
}

export interface LazyLoadingGetterParams {
  limit: number;
  offsets: LazyLoadingOffsets;
  searchTerm?: string;
  options?: Record<string, any>;
}

export interface LazyLoadingInterface {
  response: any[];
  offsetsData: LazyLoadingOffsets;
}

export type LazyLoadingResponse = LazyLoadingInterface | undefined;

export interface GenerateImageParams {
  prompt: string;
  aspectRatio?: string;
  style?: string;
  category?: string;
  providerId?: AiImageModelOption;
}

export type StockBackendData =
  | PMWStockBackendShape
  | PMWStockBackendAnimatedSticker
  | PMWStockBackendStillSticker
  | PMWStockBackendEmoji
  | PMWStockBackendIcon
  | PMWStockBackendExtractedGettySticker
  | PMWStockBackendAiSticker
  | PMWStockBackendAiIcon;

export interface StockResponse {
  offsets: StockOffsets;
  stock: StockBackendData[];
}

export interface AIGenerateImageBackendVO {
  id: number;
  filename: string;
  ext: string;
  src: string;
  hasTransparency: boolean;
  uploaderId: number;
  uploaderName: string;
  width: string;
  height: string;
  text: string;
  style: string;
  idPrompt: number;
  idAIGeneratedImage: number;
  isAddedOnDesign: boolean;
}

export interface BaseImageResponseBackendData {
  src: string;
  ext: string;
  height: number;
  width: number;
}

export interface AITextToImageBackendData {
  id: string;
  prompt: AITextToImagePromptBackendData;
  hashedFilename: string;
  addedToDesign: boolean;
  imageProps: BaseImageResponseBackendData;
  uploaderId: number;
  uploaderName: string;
}

export interface AITextToImagePromptBackendData {
  idPrompt: string;
  text: string;
  style?: string;
  aspect_ratio?: string;
}

export interface GenerateAIImageResponse {
  remainingCredits: number;
  generatedImages: AITextToImageBackendData[];
}

interface GetStockForCarouselParam {
  tabTiers: CarouselTabTiers;
}

export const getPMWStockAnimatedStickerThumbURL = (hashedFilename: string): string => {
  return repoURL(
    `${getDirPathForPMWStockMedia(PMWStockMediaSource.PMW_ANIMATED_STICKER, MediaItemSizeType.THUMB)}/${hashedFilename}.${getPMWStockAnimatedStickerThumbExtension()}`,
    getWriteBucket()
  );
};

export const getPMWStockShapeThumbURL = (hashedFilename: string): string => {
  return repoURL(
    `${getDirPathForPMWStockMedia(
      PMWStockMediaSource.PMW_SHAPE,
      MediaItemSizeType.HIGH_RES
    )}/${hashedFilename}${SHAPE_IMAGE_THUMB_FILENAME_SUFFIX}.${getCompatibleImageFileExtension(SHAPE_IMAGE_THUMB_EXTENSION)}`,
    getWriteBucket()
  );
};

export const getPMWStockIconThumbURL = (hashedFilename: string): string => {
  return getUrlForPMWStockImage(hashedFilename, PMWStockImageSource.PMW_ICON, MediaItemSizeType.THUMB);
};

export const getPMWStockAiIconThumbURL = (hashedFilename: string): string => {
  return getUrlForPMWStockImage(hashedFilename, PMWStockImageSource.PMW_AI_ICON, MediaItemSizeType.THUMB);
};

export const getPMWStockExtractedGettyStickerThumbURL = (hashedFilename: string): string => {
  return getUrlForPMWStockImage(hashedFilename, PMWStockImageSource.PMW_EXTRACTED_GETTY_STICKER, MediaItemSizeType.THUMB);
};

export const getPMWStockStillStickerThumbURL = (hashedFilename: string): string => {
  return getUrlForPMWStockImage(hashedFilename, PMWStockImageSource.PMW_STILL_STICKER, MediaItemSizeType.THUMB);
};

export const getPMWStockAiStickerThumbURL = (hashedFilename: string): string => {
  return getUrlForPMWStockImage(hashedFilename, PMWStockImageSource.PMW_AI_STICKER, MediaItemSizeType.THUMB);
};

export const getPMWStockImageThumbURL = (hashedFilename: string): string => {
  return getUrlForPMWStockImage(hashedFilename, PMWStockImageSource.PMW_STOCK_IMAGE, MediaItemSizeType.THUMB);
};

export const getPMWStockEmojiThumbURL = (hashedFilename: string): string => {
  return getUrlForPMWStockImage(hashedFilename, PMWStockImageSource.PMW_EMOJI, MediaItemSizeType.THUMB);
};

export const getPMWStockAiAnimatedStickerThumbURL = (hashedFilename: string): string => {
  return repoURL(
    `${getDirPathForPMWStockMedia(PMWStockMediaSource.PMW_AI_ANIMATED_STICKER, MediaItemSizeType.THUMB)}/${hashedFilename}.${STICKER_THUMB_EXTENSION}`,
    getWriteBucket()
  );
};

export const repoPMWStockAnimatedStickerThumbImageURL = (hashedFilename: string): string => {
  return repoURL(
    `${getDirPathForPMWStockMedia(PMWStockMediaSource.PMW_ANIMATED_STICKER, MediaItemSizeType.THUMB)}/${hashedFilename}.${STICKER_IMAGE_THUMB_EXTENSION}?ts=${STICKERS_UPDATED_ON}`,
    getWriteBucket()
  );
};

export const repoPMWStockAnimatedStickerScreenURL = (hashedFilename: string): string => {
  return repoURL(
    `${getDirPathForPMWStockMedia(
      PMWStockMediaSource.PMW_ANIMATED_STICKER,
      MediaItemSizeType.SCREEN
    )}/${hashedFilename}.${getPMWStockAnimatedStickerExtension()}?ts=${STICKERS_UPDATED_ON}`,
    getReadBucket()
  );
};

export const repoPMWStockAnimatedStickerHighResURL = (hashedFilename: string): string => {
  return repoURL(
    `${getDirPathForPMWStockMedia(
      PMWStockMediaSource.PMW_ANIMATED_STICKER,
      MediaItemSizeType.HIGH_RES
    )}/${hashedFilename}.${getPMWStockAnimatedStickerExtension()}?ts=${STICKERS_UPDATED_ON}`,
    getReadBucket()
  );
};

const getPMWStockAnimatedStickerThumbExtension = (): string => {
  return STICKER_THUMB_EXTENSION;
};

const getPMWStockAnimatedStickerExtension = (): string => {
  if (isWebmSupported()) {
    return DEFAULT_STICKER_EXTENSION;
  }

  if (isProResSupported()) {
    return TRANSPARENT_VIDEO_FALLBACK_EXTENSION;
  }

  return NON_TRANSPARENT_VIDEO_FALLBACK_EXTENSION;
};

export const getUrlForPMWStockImage = (hashedFilname: string, source: PMWStockImageSourceType, sizeType: MediaItemSizeType): string => {
  return repoURL(`${getDirPathForPMWStockMedia(source, sizeType)}/${getPMWStockImageFilename(hashedFilname, sizeType)}?ts=${PMW_STOCK_UPDATED_ON}`, getWriteBucket());
};

export const getPMWStockImageFilename = (hashedFilname: string, sizeType?: MediaItemSizeType | null): string => {
  return `${hashedFilname}.${sizeType === MediaItemSizeType.THUMB ? getCompatibleImageFileExtension(PMW_STOCK_IMAGE_THUMB_EXTENSION) : getCompatibleImageFileExtension(PMW_STOCK_IMAGE_EXTENSION)}`;
};

export const getDirPathForPMWStockMedia = (source: PMWStockMediaSourceType, sizeType: MediaItemSizeType): string => {
  let stockDir = '';
  switch (source) {
    case PMWStockMediaSource.PMW_ICON:
      stockDir = 'pmwstockicons';
      break;
    case PMWStockMediaSource.PMW_AI_ICON:
      stockDir = 'pmwstockaiicons';
      break;
    case PMWStockMediaSource.PMW_EXTRACTED_GETTY_STICKER:
      stockDir = 'pmwstockextractedgettystickers';
      break;
    case PMWStockMediaSource.PMW_STILL_STICKER:
      stockDir = 'pmwstockstillstickers';
      break;
    case PMWStockMediaSource.PMW_AI_STICKER:
      stockDir = 'pmwstockaistickers';
      break;
    case PMWStockMediaSource.PMW_STOCK_IMAGE:
      stockDir = 'pmwstockimages';
      break;
    case PMWStockMediaSource.PMW_ANIMATED_STICKER:
      stockDir = 'pmwstockanimatedstickers';
      break;
    case PMWStockMediaSource.PMW_AI_ANIMATED_STICKER:
      stockDir = 'pmwstockaianimatedstickers';
      break;
    case PMWStockMediaSource.PMW_SHAPE:
      stockDir = 'pmwstockshapes';
      break;
    default:
      throw new Error(`unhandled PMWStockMediaSource: ${source}`);
  }

  const subDirForSize = getDirForMediaSizeType(sizeType);
  return `${BASE_DIR}/${stockDir}${subDirForSize ? `/${subDirForSize}` : ''}`;
};

export const getStockForCarousel = async (p: GetStockForCarouselParam): Promise<StockResponseCarousel> => {
  const params: Record<string, string> = {
    tabTiers: JSON.stringify(p.tabTiers),
  };
  return (await window.PMW.pollingAjaxCallAsync(fetchStockForCarousel.bind(null, params), getStatusForStockCarouselGetRequest)) as StockResponseCarousel;
};

export const getStockLibraryAudioCategories = async (): Promise<AudioCategoryBackendData[]> => {
  return (await window.PMW.readLocal('posterbuilder/getStockLibraryAudioCategories', {}, AUDIO_CATEGORIES_REQUEST_LOCAL_STORAGE_CACHE_KEY)) as AudioCategoryBackendData[];
};

export const setRecentStockItem = async (stockItem: GridItemStorage): Promise<number> => {
  const {bottomContent, ...safeItem} = stockItem;

  return (await window.PMW.writeLocal('stockmedia/addItemToRecents', {stockItem: safeItem})) as number;
};

export const getRecentStockItems = async (): Promise<GridItemStorage[]> => {
  return (await window.PMW.readLocal('stockmedia/getRecentStockItems')) as GridItemStorage[];
};

export const generateImage = async (generateImageParams: GenerateImageParams): Promise<GenerateAIImageResponse> => {
  trackPosterBuilderGA4Events(GA4EventName.AI_IMAGE_TOTAL_GENERATIONS);
  return (await window.PMW.writeLocal('stockmedia/generateImage', generateImageParams)) as GenerateAIImageResponse;
};

export const getGeneratedImagesForCarousel = async (): Promise<AITextToImageBackendData[]> => {
  return (await window.PMW.readLocal('stockmedia/getGeneratedImagesForCarousel')) as AITextToImageBackendData[];
};

export const getUserGeneratedImages = async (offset: number, limit: number): Promise<AITextToImageBackendData[]> => {
  return (await window.PMW.readLocal('stockmedia/getGeneratedImages', {limit, offset})) as AITextToImageBackendData[];
};

export const getRemainingAiUsages = async (): Promise<number> => {
  return (await window.PMW.readLocal('stockmedia/getRemainingCreditsForUser')) as number;
};

export const shouldHideClipartItem = (item: GridItemStorage): boolean => {
  return !!(item.hidden && item.source === SOURCE.GETTY_ILLUSTRATIONS);
};

export const updateAiCredits = (): void => {
  getRemainingAiUsages()
    .then((remainingCredits) => {
      window.PMW.redux.store.dispatch(setAiCredits(remainingCredits));
    })
    .catch((e: unknown) => {
      console.error(e);
    });
};
